// src/components/AdminDashboard.js
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchUserProfile } from '../redux/slices/authSlice';
import api from '../utils/api';
import { showToast } from '../utils/toast';
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from './ui/card';

// Import our new components
import MemberList from './admin/MemberList';
import InviteMembersForm from './admin/InviteMembersForm';
import OrganizationSettingsForm from './admin/OrganizationSettingsForm';

// Import toggle CSS styles
import '../styles/toggle.css';

const AdminDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, isAuthenticated, loading } = useSelector((state) => state.auth);
  const [organization, setOrganization] = useState(null);
  const [members, setMembers] = useState([]);
  const [activeTab, setActiveTab] = useState('overview'); // overview, members, perks, settings
  const [loadingData, setLoadingData] = useState(true);
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        if (isAuthenticated) {
          await dispatch(fetchUserProfile());
          
          // Get organization details
          const orgResponse = await api.get('/organizations');
          setOrganization(orgResponse.data);
          
          // Get organization members
          const membersResponse = await api.get('/organizations/members');
          setMembers(membersResponse.data);
        }
      } catch (error) {
        console.error('Error fetching admin data:', error);
        showToast.error('Failed to load administrative data');
      } finally {
        setLoadingData(false);
      }
    };
    
    fetchData();
  }, [dispatch, isAuthenticated]);
  
  // Function to check if user is admin
  const isAdmin = () => {
    return user && user.role === 'admin';
  };
  
  // Handler for when member is removed
  const handleMemberRemoved = (memberId) => {
    setMembers(currentMembers => currentMembers.filter(member => member._id !== memberId));
  };
  
  // Handler for when settings are updated
  const handleSettingsUpdated = (updatedOrg) => {
    setOrganization(updatedOrg);
  };
  
  // Handler for when members are invited
  const handleMembersInvited = async () => {
    try {
      // Refresh member list
      const membersResponse = await api.get('/organizations/members');
      setMembers(membersResponse.data);
    } catch (error) {
      console.error('Error refreshing members:', error);
    }
  };
  
  // Handler for when a member is edited
  const handleEditMember = async (updatedMember) => {
    try {
      // Update the members list with the updated member
      setMembers(currentMembers => 
        currentMembers.map(member => 
          member._id === updatedMember._id ? updatedMember : member
        )
      );
      
      showToast.success(`${updatedMember.name}'s role updated successfully`);
    } catch (error) {
      console.error('Error updating member:', error);
      showToast.error('Failed to update member role');
    }
  };
  
  if (loading || loadingData) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  if (!isAuthenticated || !user || !user.organization || !isAdmin()) {
    return (
      <div className="max-w-md mx-auto mt-10 p-6 bg-white rounded-lg shadow-md">
        <h2 className="text-xl font-bold text-center mb-4">Access Denied</h2>
        <p className="text-center text-gray-600">
          You need to be logged in as an organization admin to access this dashboard.
        </p>
        <div className="mt-6 flex justify-center">
          <button
            onClick={() => navigate('/login')}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Log In
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex">
              <div className="flex-shrink-0 flex items-center">
                <h1 className="text-xl font-bold text-gray-900">
                  {organization?.name} Admin
                </h1>
              </div>
            </div>
            <div className="flex items-center">
              <button
                onClick={() => navigate('/marketplace')}
                className="px-3 py-1 text-sm text-gray-700 hover:text-gray-900"
              >
                Back to Marketplace
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex space-x-4 mb-6">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-4 py-2 text-sm font-medium rounded-md ${
              activeTab === 'overview'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('members')}
            className={`px-4 py-2 text-sm font-medium rounded-md ${
              activeTab === 'members'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Team Members
          </button>
          <button
            onClick={() => setActiveTab('perks')}
            className={`px-4 py-2 text-sm font-medium rounded-md ${
              activeTab === 'perks'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Perk Management
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`px-4 py-2 text-sm font-medium rounded-md ${
              activeTab === 'settings'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Settings
          </button>
        </div>
        
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Organization Status</CardTitle>
                <CardDescription>
                  {organization?.verificationStatus === 'verified'
                    ? 'Your organization is verified'
                    : 'Domain verification pending'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <p className="text-sm text-gray-500">Domain: {organization?.domain}</p>
                  <p className="text-sm text-gray-500">
                    Members: {members?.length || 0}
                  </p>
                  <p className="text-sm text-gray-500">
                    Plan: {organization?.subscription?.plan || 'Free'}
                  </p>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <button
                    onClick={() => setActiveTab('members')}
                    className="w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-md"
                  >
                    Invite team members
                  </button>
                  <button
                    onClick={() => setActiveTab('perks')}
                    className="w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-md"
                  >
                    Manage perks
                  </button>
                  <button
                    onClick={() => setActiveTab('settings')}
                    className="w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-md"
                  >
                    Configure settings
                  </button>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Getting Started</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <p className="text-sm text-gray-600">
                    1. Invite your team members
                  </p>
                  <p className="text-sm text-gray-600">
                    2. Set up company trading policies
                  </p>
                  <p className="text-sm text-gray-600">
                    3. Add company-wide perks
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        
        {activeTab === 'members' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-900">Team Members</h2>
              <button
                onClick={() => document.getElementById('inviteMembersForm').scrollIntoView({ behavior: 'smooth' })}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Invite Members
              </button>
            </div>
            
            <MemberList 
              members={members}
              currentUserId={user._id}
              onMemberRemoved={handleMemberRemoved}
              onEditMember={handleEditMember}
            />
            
            <div id="inviteMembersForm" className="mt-8">
              <InviteMembersForm onMembersInvited={handleMembersInvited} />
            </div>
          </div>
        )}
        
        {activeTab === 'perks' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-900">Perk Management</h2>
              <div className="space-x-3">
                <button className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">
                  Bulk Upload
                </button>
                <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
                  Add Perk
                </button>
              </div>
            </div>
            
            <Card>
              <CardHeader>
                <CardTitle>Trading Policies</CardTitle>
                <CardDescription>
                  Configure how perks can be traded within your organization
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-medium text-gray-900">Allow internal trading</h3>
                      <p className="text-xs text-gray-500">
                        Enable employees to trade perks with others in your organization
                      </p>
                    </div>
                    <div className="relative inline-block w-10 mr-2 align-middle select-none">
                      <input 
                        type="checkbox" 
                        id="perksAllowInternalTrading"
                        checked={organization?.tradingPolicies?.allowInternalTrading !== false} 
                        onChange={() => {
                          showToast.info('Use the Settings tab to change trading policies');
                        }}
                        className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                      />
                      <label htmlFor="perksAllowInternalTrading" className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"></label>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-medium text-gray-900">Allow external trading</h3>
                      <p className="text-xs text-gray-500">
                        Enable employees to trade perks with other companies
                      </p>
                    </div>
                    <div className="relative inline-block w-10 mr-2 align-middle select-none">
                      <input 
                        type="checkbox" 
                        id="perksAllowExternalTrading"
                        checked={organization?.tradingPolicies?.allowExternalTrading !== false}
                        onChange={() => {
                          showToast.info('Use the Settings tab to change trading policies');
                        }}
                        className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                      />
                      <label htmlFor="perksAllowExternalTrading" className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"></label>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-medium text-gray-900">Require admin approval for perks</h3>
                      <p className="text-xs text-gray-500">
                        Admins must approve perks before they can be traded
                      </p>
                    </div>
                    <div className="relative inline-block w-10 mr-2 align-middle select-none">
                      <input 
                        type="checkbox" 
                        id="perksRequireAdminApprovalForPerks"
                        checked={organization?.tradingPolicies?.requireAdminApprovalForPerks || false}
                        onChange={() => {
                          showToast.info('Use the Settings tab to change trading policies');
                        }}
                        className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                      />
                      <label htmlFor="perksRequireAdminApprovalForPerks" className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"></label>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-medium text-gray-900">Require admin approval for trades</h3>
                      <p className="text-xs text-gray-500">
                        Admins must approve trades before they can be completed
                      </p>
                    </div>
                    <div className="relative inline-block w-10 mr-2 align-middle select-none">
                      <input 
                        type="checkbox" 
                        id="perksRequireAdminApprovalForTrades"
                        checked={organization?.tradingPolicies?.requireAdminApprovalForTrades || false}
                        onChange={() => {
                          showToast.info('Use the Settings tab to change trading policies');
                        }}
                        className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                      />
                      <label htmlFor="perksRequireAdminApprovalForTrades" className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"></label>
                    </div>
                  </div>
                  
                  <div className="pt-4">
                    <button 
                      onClick={() => setActiveTab('settings')}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                      Manage Policies
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <div className="mt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Pending Approvals</h3>
              <div className="bg-white shadow rounded-lg p-6 text-center text-gray-500">
                No pending perk approvals
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'settings' && (
          <OrganizationSettingsForm 
            organization={organization}
            onSettingsUpdated={handleSettingsUpdated}
          />
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;